import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import dealerService from "../services/dealerService";
import CreateDealerModal from "../components/CreateDealerModal";
import ViewDealerModal from "../components/ViewDealerModal";

const PAGE_SIZE = 10;

const Dealers = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showCreateDealer, setShowCreateDealer] = useState(false);
  const [selectedDealer, setSelectedDealer] = useState(null);
  const [statusLoadingId, setStatusLoadingId] = useState(null);
  const [dealerToDelete, setDealerToDelete] = useState(null);
  const [deletingDealer, setDeletingDealer] = useState(false);

  const toggleSidebar = () => {
    setSidebarOpen((previous) => !previous);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };


  const handleDeleteDealer = async (dealer) => {
    if (!dealer) return;
    try {
      setDeletingDealer(true);
      const res = await dealerService.deleteDealer(dealer.id);
      if (!res?.success) {
        toast.error(res?.message || "Failed to delete Dealer.");
        return;
      }
      toast.success(res.message || "Dealer deleted successfully.");
      setDealerToDelete(null);
      if (selectedDealer?.id === dealer.id) {
        setSelectedDealer(null);
      }
      await loadDealers();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to delete Dealer."
      );
    } finally {
      setDeletingDealer(false);
    }
  };

  const loadDealers = async () => {
    try {
      setLoading(true);

      const data = await dealerService.getDealers();

      if (data?.success) {
        setDealers(Array.isArray(data.dealers) ? data.dealers : []);
      } else {
        setDealers([]);
        toast.error(data?.message || "Unable to load dealers.");
      }
    } catch (error) {
      console.error("Load Dealers Error:", error);

      toast.error(
        error.response?.data?.message || "Failed to load dealers."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDealers();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const filteredDealers = useMemo(() => {
    const value = search.trim().toLowerCase();

    if (!value) {
      return dealers;
    }

    return dealers.filter((dealer) => {
      const fullName = String(dealer.full_name || "").toLowerCase();
      const username = String(dealer.username || "").toLowerCase();
      const mobile = String(dealer.mobile || "").toLowerCase();
      const status = String(dealer.status || "").toLowerCase();

      return (
        fullName.includes(value) ||
        username.includes(value) ||
        mobile.includes(value) ||
        status.includes(value)
      );
    });
  }, [dealers, search]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredDealers.length / PAGE_SIZE)
  );

  const safeCurrentPage = Math.min(currentPage, totalPages);

  const paginatedDealers = useMemo(() => {
    const startIndex = (safeCurrentPage - 1) * PAGE_SIZE;
    return filteredDealers.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filteredDealers, safeCurrentPage]);

  const activeDealers = dealers.filter(
    (dealer) => dealer.status === "ACTIVE"
  ).length;

  const blockedDealers = dealers.filter(
    (dealer) => dealer.status === "BLOCKED"
  ).length;

  const totalDealerWallet = dealers.reduce(
    (total, dealer) => total + Number(dealer.wallet || 0),
    0
  );

  const formatCoins = (amount) => {
    return Number(amount || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const formatDate = (date) => {
    if (!date) {
      return "-";
    }

    return new Date(date).toLocaleString("en-IN");
  };

  const handleStatusChange = async (dealer) => {
    const nextStatus = dealer.status === "ACTIVE" ? "BLOCKED" : "ACTIVE";
    const action = nextStatus === "ACTIVE" ? "unblock" : "block";

    try {
      setStatusLoadingId(dealer.id);
      const data = await dealerService.updateDealerStatus({
        dealer_id: dealer.id,
        status: nextStatus
      });
      toast.success(data?.message || `Dealer ${action}ed successfully.`);
      await loadDealers();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.message || `Unable to ${action} Dealer.`);
      return false;
    } finally {
      setStatusLoadingId(null);
    }
  };

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) {
      return;
    }

    setCurrentPage(page);
  };

  const startRecord =
    filteredDealers.length === 0
      ? 0
      : (safeCurrentPage - 1) * PAGE_SIZE + 1;

  const endRecord = Math.min(
    safeCurrentPage * PAGE_SIZE,
    filteredDealers.length
  );

  return (
    <div className="d-flex flex-column min-vh-100 app-shell">
      <Navbar toggleSidebar={toggleSidebar} />

      <div className="d-flex flex-grow-1 position-relative">
        <Sidebar
          isOpen={sidebarOpen}
          closeMobileSidebar={closeSidebar}
        />

        <main className="app-main flex-grow-1 overflow-auto">
          <div className="container-fluid admin-page-wrap">
            <div className="admin-page-header">
              <div>
                <span className="admin-page-kicker">ADMIN DASHBOARD</span>
                <h2>Dealer Management</h2>

                <p>
                  View dealers and manage dealer coin transfers.
                </p>
              </div>

              <div className="d-flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={loadDealers}
                  disabled={loading}
                >
                  <i className="bi bi-arrow-clockwise me-2" />
                  Refresh
                </button>

                <button
                  type="button"
                  className="btn btn-dark"
                  onClick={() => setShowCreateDealer(true)}
                >
                  <i className="bi bi-person-plus-fill me-2" />
                  Add Dealer
                </button>

                <Link
                  to="/transfer-dealer"
                  className="btn btn-primary"
                >
                  <i className="bi bi-arrow-left-right me-2" />
                  Transfer Coins
                </Link>
              </div>
            </div>

            <div className="row g-3 mb-4">
              <div className="col-12 col-sm-6 col-xl-3">
                <div className="admin-stat-card h-100">
                  <div className="card-body d-flex align-items-center">
                    <div className="bg-primary text-white rounded-3 p-3 me-3">
                      <i className="bi bi-people-fill fs-3" />
                    </div>

                    <div>
                      <div className="small text-secondary fw-semibold">
                        Total Dealers
                      </div>

                      <h3 className="fw-bold mb-0">
                        {dealers.length}
                      </h3>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-12 col-sm-6 col-xl-3">
                <div className="admin-stat-card h-100">
                  <div className="card-body d-flex align-items-center">
                    <div className="bg-success text-white rounded-3 p-3 me-3">
                      <i className="bi bi-person-check-fill fs-3" />
                    </div>

                    <div>
                      <div className="small text-secondary fw-semibold">
                        Active Dealers
                      </div>

                      <h3 className="fw-bold mb-0">
                        {activeDealers}
                      </h3>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-12 col-sm-6 col-xl-3">
                <div className="admin-stat-card h-100">
                  <div className="card-body d-flex align-items-center">
                    <div className="bg-danger text-white rounded-3 p-3 me-3">
                      <i className="bi bi-person-x-fill fs-3" />
                    </div>

                    <div>
                      <div className="small text-secondary fw-semibold">
                        Blocked Dealers
                      </div>

                      <h3 className="fw-bold mb-0">
                        {blockedDealers}
                      </h3>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-12 col-sm-6 col-xl-3">
                <div className="admin-stat-card h-100">
                  <div className="card-body d-flex align-items-center">
                    <div className="bg-warning text-dark rounded-3 p-3 me-3">
                      <i className="bi bi-wallet2 fs-3" />
                    </div>

                    <div>
                      <div className="small text-secondary fw-semibold">
                        Dealer Wallet Total
                      </div>

                      <h5 className="fw-bold mb-0">
                        🪙 {formatCoins(totalDealerWallet)}
                      </h5>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="admin-panel mb-4">
              <div className="card-body p-3">
                <div className="input-group" style={{ maxWidth: "500px" }}>
                  <span className="input-group-text bg-white">
                    <i className="bi bi-search" />
                  </span>

                  <input
                    type="search"
                    className="form-control"
                    placeholder="Search name, username, mobile or status..."
                    value={search}
                    onChange={(event) =>
                      setSearch(event.target.value)
                    }
                  />

                  {search && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setSearch("")}
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="admin-panel overflow-hidden">
              <div className="admin-panel-head">
                <h5 className="fw-bold mb-1">
                  Dealer List
                </h5>

                <p className="small text-secondary mb-0">
                  Click any Dealer row to view details and available actions.
                </p>
              </div>

              <div className="card-body p-0">
                {loading ? (
                  <div className="text-center py-5">
                    <div
                      className="spinner-border text-primary"
                      role="status"
                    >
                      <span className="visually-hidden">
                        Loading...
                      </span>
                    </div>

                    <p className="text-secondary mt-3 mb-0">
                      Loading dealers...
                    </p>
                  </div>
                ) : paginatedDealers.length === 0 ? (
                  <div className="text-center py-5 px-3">
                    <i className="bi bi-people fs-1 text-secondary" />

                    <h5 className="mt-3">
                      No dealers found
                    </h5>

                    <p className="text-secondary mb-0">
                      {search
                        ? "Try a different search value."
                        : "No Dealer accounts are available."}
                    </p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table admin-table table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th className="ps-4">Sr No</th>
                          <th>Dealer</th>
                          <th>Mobile</th>
                          <th>Wallet</th>
                          <th>Shops</th>
                          <th>Created</th>
                          <th className="pe-4">Status</th>
                        </tr>
                      </thead>

                      <tbody>
                        {paginatedDealers.map((dealer, index) => (
                          <tr
                            key={dealer.id}
                            onClick={() => setSelectedDealer(dealer)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                setSelectedDealer(dealer);
                              }
                            }}
                            role="button"
                            tabIndex={0}
                            title="Click to view Dealer details"
                            style={{ cursor: "pointer" }}
                          >
                            <td className="ps-4 fw-bold">
                              {startRecord + index}
                            </td>

                            <td>
                              <div className="fw-semibold">
                                {dealer.full_name || "Unnamed Dealer"}
                              </div>

                              <div className="small text-primary">
                                @{dealer.username}
                              </div>
                            </td>

                            <td>{dealer.mobile || "-"}</td>

                            <td className="fw-bold text-success">
                              🪙 {formatCoins(dealer.wallet)}
                            </td>

                            <td>
                              <span className="badge rounded-pill bg-info text-dark px-3 py-1.5 fw-bold">
                                {Number(dealer.total_shops || 0)} Shops
                              </span>
                            </td>

                            <td className="small text-secondary">
                              {formatDate(dealer.created_at)}
                            </td>

                            <td className="pe-4">
                              <div className="d-flex align-items-center justify-content-between gap-3">
                                <span
                                  className={`badge rounded-pill px-3 py-2 ${
                                    dealer.status === "ACTIVE"
                                      ? "bg-success-subtle text-success border border-success-subtle"
                                      : "bg-danger-subtle text-danger border border-danger-subtle"
                                  }`}
                                >
                                  {dealer.status || "UNKNOWN"}
                                </span>

                                <i className="bi bi-chevron-right text-secondary small" />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {!loading && filteredDealers.length > 0 && (
                <div className="card-footer bg-white border-top p-3">
                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                    <div className="small text-secondary">
                      Showing {startRecord}–{endRecord} of{" "}
                      {filteredDealers.length} dealers
                    </div>

                    <nav aria-label="Dealer pagination">
                      <ul className="pagination pagination-sm mb-0">
                        <li
                          className={`page-item ${safeCurrentPage === 1
                            ? "disabled"
                            : ""
                            }`}
                        >
                          <button
                            type="button"
                            className="page-link"
                            onClick={() =>
                              goToPage(safeCurrentPage - 1)
                            }
                          >
                            Previous
                          </button>
                        </li>

                        {Array.from(
                          { length: totalPages },
                          (_, index) => index + 1
                        ).map((page) => (
                          <li
                            key={page}
                            className={`page-item ${safeCurrentPage === page
                              ? "active"
                              : ""
                              }`}
                          >
                            <button
                              type="button"
                              className="page-link"
                              onClick={() => goToPage(page)}
                            >
                              {page}
                            </button>
                          </li>
                        ))}

                        <li
                          className={`page-item ${safeCurrentPage === totalPages
                            ? "disabled"
                            : ""
                            }`}
                        >
                          <button
                            type="button"
                            className="page-link"
                            onClick={() =>
                              goToPage(safeCurrentPage + 1)
                            }
                          >
                            Next
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {showCreateDealer && (
        <CreateDealerModal
          show={showCreateDealer}
          onClose={() => setShowCreateDealer(false)}
          onDealerCreated={loadDealers}
        />
      )}

      <ViewDealerModal
        show={Boolean(selectedDealer)}
        dealer={selectedDealer}
        onClose={() => setSelectedDealer(null)}
        onToggleStatus={handleStatusChange}
        isUpdatingStatus={statusLoadingId === selectedDealer?.id}
        onDeleteDealer={(dealer) => setDealerToDelete(dealer)}
      />

      {/* =====================================================
          CONFIRM DELETE DEALER MODAL
      ===================================================== */}
      {dealerToDelete && (
        <>
          <div
            className="modal-backdrop fade show"
            style={{ zIndex: 1060 }}
          />

          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            style={{ zIndex: 1065 }}
            onMouseDown={(event) => {
              if (event.target === event.currentTarget && !deletingDealer) {
                setDealerToDelete(null);
              }
            }}
          >
            <div
              className="modal-dialog modal-dialog-centered"
              style={{ maxWidth: "440px", margin: "10px auto" }}
            >
              <div className="modal-content border border-[rgba(239,68,68,0.3)] bg-[#121827] shadow-2xl rounded-2xl overflow-hidden text-white">
                <div className="modal-header bg-[#0D111C] border-b border-[rgba(148,163,184,0.14)] py-3 px-4 d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-2 text-danger">
                    <i className="bi bi-exclamation-triangle-fill fs-5" />
                    <h5 className="modal-title fw-bold fs-6 mb-0 text-white">
                      Confirm Delete Dealer
                    </h5>
                  </div>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    aria-label="Close"
                    disabled={deletingDealer}
                    onClick={() => setDealerToDelete(null)}
                  />
                </div>

                <div className="modal-body p-4 text-center">
                  <div className="mb-3">
                    <div className="d-inline-flex p-3 rounded-circle bg-danger bg-opacity-10 text-danger mb-2">
                      <i className="bi bi-trash fs-2" />
                    </div>
                  </div>

                  <h6 className="fw-bold mb-2 fs-5">
                    Delete "{dealerToDelete.full_name || dealerToDelete.username}"?
                  </h6>

                  <p className="text-secondary small mb-3">
                    Are you sure you want to permanently delete this Dealer?
                    All associated shops, computers, players, and betting history under this Dealer will be permanently deleted.
                  </p>

                  <div className="p-3 rounded-xl bg-[#0D111C] border border-[rgba(148,163,184,0.14)] text-start small">
                    <div className="d-flex justify-content-between mb-1">
                      <span className="text-secondary">Dealer ID:</span>
                      <span className="fw-bold">#{dealerToDelete.id}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-1">
                      <span className="text-secondary">Username:</span>
                      <span className="fw-bold text-primary">@{dealerToDelete.username}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-1">
                      <span className="text-secondary">Total Shops:</span>
                      <span className="fw-bold text-info">{Number(dealerToDelete.total_shops || 0)} Shops</span>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span className="text-secondary">Wallet Coins:</span>
                      <span className="fw-bold text-warning">🪙 {Number(dealerToDelete.wallet || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                </div>

                <div className="modal-footer bg-[#0D111C] border-t border-[rgba(148,163,184,0.14)] py-3 px-4 d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    className="btn btn-secondary text-xs px-3.5 py-2"
                    disabled={deletingDealer}
                    onClick={() => setDealerToDelete(null)}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    className="btn btn-danger text-xs px-4 py-2 fw-bold"
                    disabled={deletingDealer}
                    onClick={() => handleDeleteDealer(dealerToDelete)}
                  >
                    {deletingDealer ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1.5" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-trash me-1.5" />
                        Yes, Delete
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Dealers;